'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { lookupAddressByCep, normalizeCep } from '@/services/cepService'
import type { CepLookupData } from '@/types/CepType'

type UseCepLookupOptions = {
  enabled?: boolean
  debounceMs?: number
}

type QueryResultState = {
  cep: string
  address: CepLookupData | null
  notFound: boolean
  error: string | null
}

const INITIAL_RESULT: QueryResultState = {
  cep: '',
  address: null,
  notFound: false,
  error: null,
}

const REQUEST_ERROR_MESSAGE = 'Não foi possível consultar o CEP. Tente novamente.'

export function useCepLookup(cep: string, options: UseCepLookupOptions = {}) {
  const { enabled = true, debounceMs = 600 } = options
  const normalizedCep = normalizeCep(cep)
  const [result, setResult] = useState<QueryResultState>(INITIAL_RESULT)
  const [searchCount, setSearchCount] = useState(0)
  const explicitSearchRef = useRef<string | null>(null)

  const canLookup = enabled && normalizedCep.length === 8
  const isCurrent = result.cep === normalizedCep

  const { address, notFound, error } = useMemo(() => {
    if (isCurrent) {
      return { address: result.address, notFound: result.notFound, error: result.error }
    }
    return { address: null, notFound: false, error: null }
  }, [isCurrent, result])

  const isLoading = canLookup && !isCurrent

  const search = useCallback(() => {
    if (!enabled) return
    explicitSearchRef.current = normalizedCep
    setResult(INITIAL_RESULT)
    setSearchCount((count) => count + 1)
  }, [enabled, normalizedCep])

  useEffect(() => {
    if (!canLookup) return

    const rawCep = normalizedCep
    let isStale = false
    const delay = explicitSearchRef.current === rawCep ? 0 : debounceMs

    const timeoutId = window.setTimeout(async () => {
      try {
        const data = await lookupAddressByCep(rawCep)
        if (!isStale) {
          setResult({ cep: rawCep, address: data, notFound: !data, error: null })
        }
      } catch {
        if (!isStale) {
          setResult({ cep: rawCep, address: null, notFound: false, error: REQUEST_ERROR_MESSAGE })
        }
      }
    }, delay)

    return () => {
      isStale = true
      window.clearTimeout(timeoutId)
    }
  }, [canLookup, normalizedCep, debounceMs, searchCount])

  return useMemo(() => ({ address, isLoading, notFound, error, search }), [
    address,
    isLoading,
    notFound,
    error,
    search,
  ])
}